import { ReactNode } from "react";
import { type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { BoxWithAnimatedBackground } from "@/components/atoms/BoxWithAnimatedBackground";
import { Screenshot } from "@/components/atoms/Screenshot";
import { Navigation } from "@/constants";
import { LayoutRouter } from "@/layouts/LayoutRouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

interface SimplePageProps {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  primaryCta?: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
  /** The hero: always the product, paints as a still then plays its clip. */
  screenshot?: { imageName: string; alt: string };
  /** Alternating crop + copy rows under the hero. */
  capabilities?: {
    title: string;
    description: string;
    imageName: string;
  }[];
  /** A landing-page illustration, played at full strength below the product. */
  illustration?: {
    title: string;
    description: string;
    render: () => ReactNode;
  };
  bullets?: {
    ReactIcon?: LucideIcon;
    name: string;
    tagline?: string;
    to?: string;
  }[];
  children?: ReactNode;
}

export function SimplePage({
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  screenshot,
  capabilities,
  illustration,
  bullets,
  children,
}: SimplePageProps) {
  const navigate = useNavigate();

  const primary = primaryCta ?? {
    label: "Join waitlist",
    to: Navigation.ROUTES.AUTH.WAITLIST,
  };

  return (
    <LayoutRouter
      isPublicLayout
      isHorizontalPaddingIncluded
      isVerticalPaddingIncluded={false}
      isFooterVisible
      isRulersIncluded={false}
      topbarMaxWidth="xl"
      backgroundColor={"var(--background)"}
      className="max-w-350"
    >
      {/* Wide enough that the hero and close-ups render at the same scale as
          the home page's. */}
      <div className="w-full max-w-350 ml-auto mr-auto flex flex-col items-start gap-6 pl-4 pr-4 pt-12 pb-16 md:pl-8 md:pr-8 md:pt-20 md:pb-24">
        {eyebrow ? (
          <Badge variant="outline" className="rounded-sm">
            {eyebrow}
          </Badge>
        ) : null}
        <Typography
          level="h1"
          className="[line-height:1.08] [letter-spacing:-0.02em] max-w-205 text-[2.25rem] md:text-[3.25rem] lg:text-[3.75rem]"
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            level="body-lg"
            className="max-w-170 text-muted-foreground"
          >
            {subtitle}
          </Typography>
        ) : null}
        <div className="flex gap-3 mt-2 flex-wrap">
          <Button
            size="lg"
            onClick={() => navigate(primary.to)}
            className=" pl-4 pr-4"
          >
            {primary.label}
          </Button>
          {secondaryCta ? (
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate(secondaryCta.to)}
              className=" pl-4 pr-4"
            >
              {secondaryCta.label} →
            </Button>
          ) : null}
        </div>

        {screenshot ? (
          <div className="w-full mt-8 md:mt-12">
            <Screenshot imageName={screenshot.imageName} alt={screenshot.alt} />
          </div>
        ) : null}

        {capabilities && capabilities.length > 0 ? (
          <div className="w-full flex flex-col mt-12 gap-16 md:mt-20 md:gap-24">
            {capabilities.map((capability, i) => (
              <div
                key={capability.imageName}
                className={cn(
                  "grid items-center grid-cols-[1fr] gap-6 lg:gap-12",
                  // The image always takes the wide column, so every close-up renders
                  // at the same size; alternating rows swap the columns, not just order.
                  // Below lg the row stacks, so the close-up keeps its full frame.
                  i % 2
                    ? "lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]"
                    : "lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]",
                )}
              >
                <div
                  className={cn(
                    i % 2 ? "order-0 lg:order-1" : "order-0 lg:order-0",
                  )}
                >
                  <Typography level="h3" className="mb-2">
                    {capability.title}
                  </Typography>
                  <Typography level="body-lg" className="text-muted-foreground">
                    {capability.description}
                  </Typography>
                </div>
                <Screenshot
                  imageName={capability.imageName}
                  alt={capability.title}
                />
              </div>
            ))}
          </div>
        ) : null}

        {illustration ? (
          <BoxWithAnimatedBackground
            fadeIn
            backgroundPattern="dots"
            className="w-full [border-width:1px] [border-style:solid] border-border rounded-md overflow-hidden mt-16 md:mt-24"
            innerClassName="grid items-center grid-cols-[1fr] gap-6 p-6 md:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] md:gap-12 md:p-12"
          >
            <div>
              <Typography
                level="body-sm"
                className="text-muted-foreground/80 mb-2 uppercase [letter-spacing:0.08em]"
              >
                How it works
              </Typography>
              <Typography level="h3" className="mb-2">
                {illustration.title}
              </Typography>
              <Typography level="body-md" className="text-muted-foreground">
                {illustration.description}
              </Typography>
            </div>
            <div
              aria-hidden
              className="flex flex-col justify-between gap-4 pointer-events-none min-h-70 md:min-h-105"
            >
              {illustration.render()}
            </div>
          </BoxWithAnimatedBackground>
        ) : null}

        {bullets && bullets.length > 0 ? (
          <div className="w-full grid gap-0 [border-top-width:1px] [border-top-style:solid] border-t-border [border-left-width:1px] [border-left-style:solid] border-l-border mt-12 grid-cols-[1fr] sm:grid-cols-2 md:mt-16">
            {bullets.map((b, i) => (
              <div
                key={i}
                onClick={b.to ? () => navigate(b.to!) : undefined}
                className={cn(
                  "[border-right-width:1px] [border-right-style:solid] border-r-border [border-bottom-width:1px] [border-bottom-style:solid] border-b-border [transition:background-color_0.15s] p-6 md:p-8",
                  b.to ? "cursor-pointer hover:bg-muted" : "cursor-default",
                )}
              >
                {b.ReactIcon ? (
                  <div className="text-muted-foreground mb-3 inline-flex">
                    <b.ReactIcon size={20} />
                  </div>
                ) : null}
                <Typography level="title-md" className="mb-1">
                  {b.name}
                </Typography>
                {b.tagline ? (
                  <Typography
                    level="body-sm"
                    className="text-muted-foreground/80"
                  >
                    {b.tagline}
                  </Typography>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {children}
      </div>
    </LayoutRouter>
  );
}
